import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { SabiasQueNY, estiloH3NY, estiloPNY } from "./nyHelpers";

// Template75: Día 2 Lower Manhattan 2 (Page11) - Wall Street, Memorial 11S, One World, Oculus. Contenido desde page.
const Template75 = ({
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const getImageSrc = (id) => {
		if (page?.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};
	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const parseSabiasQueItems = (html) => {
		if (!html) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, "text/html");
		const ps = doc.querySelectorAll("p");
		const items = [];
		ps.forEach((p) => {
			const text = (p.textContent ?? p.innerText ?? "").trim();
			if (!text) return;
			const emojiMatch = text.match(/^([^\s]+)\s+(.*)/s);
			if (emojiMatch)
				items.push({ icono: emojiMatch[1], texto: emojiMatch[2].trim() });
		});
		return items;
	};

	const title = page?.texts?.[0]?.content;
	const wallTitle = page?.texts?.[1]?.content;
	const wallBody = page?.texts?.[2]?.content;
	const trinityTitle = page?.texts?.[3]?.content;
	const trinityBody = page?.texts?.[4]?.content;
	const memorialTitle = page?.texts?.[5]?.content;
	const memorialBody = page?.texts?.[6]?.content;
	const oneWorldTitle = page?.texts?.[7]?.content;
	const oneWorldBody = page?.texts?.[8]?.content;
	const libertyTitle = page?.texts?.[9]?.content;
	const libertyBody = page?.texts?.[10]?.content;
	const oculusTitle = page?.texts?.[11]?.content;
	const oculusBody = page?.texts?.[12]?.content;
	const oculusTransport = page?.texts?.[13]?.content;
	const sabiasQueItems = parseSabiasQueItems(page?.texts?.[14]?.content);
	const yellowNina = page?.texts?.[15]?.content;
	const yellowCentury = page?.texts?.[16]?.content;

	const wallSrc = getImageSrc("wallstreet");
	const sinMiedoSrc = getImageSrc("sin-miedo");
	const oneWorldSrc = getImageSrc("one-trade");
	const oculusSrc = getImageSrc("oculus");
	const centurySrc = getImageSrc("century");
	const logoSrc = getCanvasImageUrl("logo");

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				backgroundColor: "#f7f7f7",
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
				"& p": { marginTop: "0px !important", marginBottom: "0px !important" },
				"& a": {
					color: "#007a8d",
					fontWeight: 700,
					textDecoration: "underline",
				},
			}}
		>
			<div
				data-block-id="header"
				style={{
					backgroundColor: headerBackgroundColor || "#59c3d8",
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontFamily: fontFamily || '"Poppins", sans-serif',
					fontSize: "14px",
					fontWeight: 700,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 9999,
				}}
			>
				{title}
				{logoSrc && (
					<img
						src={logoSrc}
						alt="logo"
						style={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</div>

			<div
				style={{
					padding: "10px 12px",
					display: "flex",
					flexDirection: "column",
					gap: 20,
					marginTop: 8,
				}}
			>
				{/* Wall Street */}
				{(wallTitle || wallBody || wallSrc) && (
					<div data-block-id="text-1" style={{ display: "flex", gap: 16, position: "relative" }}>
						{wallSrc && (
							<img
								src={wallSrc}
								alt="Wall Street"
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						)}
						{sabiasQueItems[0] && (
							<div style={{ position: "absolute", top: 0, left: "35%" }}>
								<SabiasQueNY
									icono={sabiasQueItems[0].icono}
									texto={sabiasQueItems[0].texto}
								/>
							</div>
						)}
						{sabiasQueItems[1] && (
							<div style={{ position: "absolute", bottom: 0, left: "35%" }}>
								<SabiasQueNY
									icono={sabiasQueItems[1].icono}
									texto={sabiasQueItems[1].texto}
								/>
							</div>
						)}
						<div style={{ width: "60%" }}>
							{wallTitle && <h3 style={estiloH3NY}>{wallTitle}</h3>}
							{wallBody && <p style={estiloPNY}>{wallBody}</p>}
						</div>
						{yellowNina && (
							<div
								style={{
									position: "absolute",
									bottom: isSmallScreen ? -80 : -48,
									right: 80,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 0px 16px",
									padding: "10px 10px",
									fontSize: 11,
									fontFamily: '"Poppins", sans-serif',
									color: "#444",
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 200,
									zIndex: 6,
								}}
							>
								{yellowNina.includes(",") ? (
									<>
										<strong>{yellowNina.split(",")[0]}, </strong>
										{yellowNina.split(",").slice(1).join(",").trim()}
									</>
								) : (
									yellowNina
								)}
							</div>
						)}
						{sinMiedoSrc && (
							<img
								src={sinMiedoSrc}
								alt="Niña sin miedo"
								style={{
									width: 100,
									position: "absolute",
									bottom: isSmallScreen ? -120 : -60,
									right: 0,
									zIndex: 5,
								}}
							/>
						)}
					</div>
				)}

				{/* Trinity Church */}
				{(trinityTitle || trinityBody) && (
					<div
						data-block-id="text-2"
						style={{
							display: "flex",
							gap: 16,
							marginTop: isSmallScreen ? 80 : 14,
							position: "relative",
						}}
					>
						<div style={{ position: "relative" }}>
							{trinityTitle && <h3 style={estiloH3NY}>{trinityTitle}</h3>}
							{trinityBody && <p style={estiloPNY}>{trinityBody}</p>}
						</div>
						{sabiasQueItems[2] && (
							<div style={{ position: "absolute", top: -15, left: "37%" }}>
								<SabiasQueNY
									icono={sabiasQueItems[2].icono}
									texto={sabiasQueItems[2].texto}
								/>
							</div>
						)}
					</div>
				)}

				{/* Memorial 11S */}
				{(memorialTitle || memorialBody) && (
					<div
						data-block-id="text-3"
						style={{
							backgroundColor: "#e0f7fa",
							padding: 12,
							borderRadius: 12,
							display: "flex",
							gap: 16,
							alignItems: "center",
							position: "relative",
						}}
					>
						<div style={{ width: "100%" }}>
							{memorialTitle && <h3 style={estiloH3NY}>{memorialTitle}</h3>}
							{memorialBody && <p style={estiloPNY}>{memorialBody}</p>}
						</div>
					</div>
				)}

				{/* One World */}
				{(oneWorldTitle || oneWorldBody || oneWorldSrc) && (
					<div data-block-id="text-4" style={{ display: "flex", gap: 16, position: "relative" }}>
						<div style={{ width: "60%", position: "relative" }}>
							{oneWorldTitle && <h3 style={estiloH3NY}>{oneWorldTitle}</h3>}
							{oneWorldBody && <p style={estiloPNY}>{oneWorldBody}</p>}
						</div>
						{oneWorldSrc && (
							<img
								src={oneWorldSrc}
								alt="One World"
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						)}
						{sabiasQueItems[3] && (
							<div style={{ position: "absolute", bottom: -10, left: "50%" }}>
								<SabiasQueNY
									icono={sabiasQueItems[3].icono}
									texto={sabiasQueItems[3].texto}
								/>
							</div>
						)}
					</div>
				)}

				{/* Liberty Park */}
				{(libertyTitle || libertyBody) && (
					<div
						data-block-id="text-5"
						style={{
							display: "flex",
							gap: 16,
							position: "relative",
							marginTop: isSmallScreen ? 0 : -30,
						}}
					>
						<div style={{ position: "relative" }}>
							{libertyTitle && <h3 style={estiloH3NY}>{libertyTitle}</h3>}
							{libertyBody && <p style={estiloPNY}>{libertyBody}</p>}
						</div>
						{sabiasQueItems[4] && (
							<div style={{ position: "absolute", top: -10, left: "30%" }}>
								<SabiasQueNY
									icono={sabiasQueItems[4].icono}
									texto={sabiasQueItems[4].texto}
								/>
							</div>
						)}
					</div>
				)}

				{/* Oculus */}
				{(oculusTitle || oculusBody || oculusSrc) && (
					<div data-block-id="text-6" style={{ display: "flex", gap: 16, position: "relative" }}>
						{oculusSrc && (
							<img
								src={oculusSrc}
								alt="Oculus"
								style={{
									width: "40%",
									height: 110,
									borderRadius: 12,
									objectFit: "cover",
								}}
							/>
						)}
						<div
							style={{
								width: "60%",
								gap: 12,
								display: "flex",
								flexDirection: "column",
							}}
						>
							<div>
								{oculusTitle && <h3 style={estiloH3NY}>{oculusTitle}</h3>}
								{oculusBody && <p style={estiloPNY}>{oculusBody}</p>}
							</div>
							{oculusTransport && (
								<div
									style={{
										backgroundColor: "#e0f7fa",
										border: "3px solid #59c3d8",
										borderRadius: 12,
										padding: "11px 20px",
										fontSize: 12,
										fontFamily: '"Poppins", sans-serif',
										color: "#333",
									}}
								>
									<p
										style={{
											fontFamily: '"Poppins", sans-serif',
											fontSize: 12,
											color: "#444",
											margin: 0,
										}}
									>
										{oculusTransport}
									</p>
								</div>
							)}
						</div>
						{sabiasQueItems[5] && (
							<div style={{ position: "absolute", top: 0, left: "35%" }}>
								<SabiasQueNY
									icono={sabiasQueItems[5].icono}
									texto={sabiasQueItems[5].texto}
								/>
							</div>
						)}
						{yellowCentury && (
							<div
								style={{
									position: "absolute",
									bottom: isSmallScreen ? 80 : 5,
									left: isSmallScreen ? 35 : 80,
									backgroundColor: "#fff8db",
									border: "2px solid #f5c400",
									borderRadius: "16px 16px 16px 0px",
									padding: "10px 10px",
									fontSize: 11,
									fontFamily: '"Poppins", sans-serif',
									color: "#444",
									boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
									maxWidth: 100,
									zIndex: 6,
								}}
							>
								{yellowCentury.includes("!") ? (
									<>
										<strong>{yellowCentury.split("!")[0]}!</strong>{" "}
										{yellowCentury.split("!").slice(1).join("!").trim()}
									</>
								) : (
									yellowCentury
								)}
							</div>
						)}
						{centurySrc && (
							<img
								src={centurySrc}
								alt="century"
								style={{
									width: 100,
									position: "absolute",
									bottom: isSmallScreen ? 0 : -10,
									left: 0,
									zIndex: 5,
								}}
							/>
						)}
					</div>
				)}
			</div>
		</Box>
	);
};

export default Template75;
